import { createEffect, createSignal, onCleanup, onMount, on } from 'solid-js';
import { useOutsideClick } from '~/utils/hooks';
import { useMenu } from './MenuContext';

export const useMenuLogic = () => {
  const menu = useMenu();
  const { isSidebarOpen, setIsSidebarOpen } = menu!;
  const toggleMenuCollapse = () => {
    menu?.setIsManuallyCollapsed(!menu.isManuallyCollapsed());
  };

  const [refMenuContainer, setRefMenuContainer] = createSignal<HTMLDivElement>();
  const [scrollContainerRef, setScrollContainerRef] = createSignal<HTMLDivElement>();
  const [menuItemsRef, setMenuItemsRef] = createSignal<HTMLDivElement>();

  const [scrollHeight, setScrollHeight] = createSignal<string>('100%');
  let resizeObserver: ResizeObserver;

  const handleResize = () => setIsSidebarOpen(window.innerWidth > 767);

  const updateContainerHeight = () => {
    if (menuItemsRef()) {
      setScrollHeight('100%');
      requestAnimationFrame(() => {
        setScrollHeight(`${menuItemsRef()?.scrollHeight}px`);
      });
    }
  };

  const scrollToTop = () => {
    const el = scrollContainerRef();
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => {
        el.scrollTop = 0;
      }, 50);
    });
  };

  createEffect(() => {
    resizeObserver = new ResizeObserver(() => {
      updateContainerHeight();
    });

    if (menuItemsRef()) {
      resizeObserver.observe(menuItemsRef()!);
      updateContainerHeight();
    }
  });

  createEffect(() => {
    if (isSidebarOpen() && menu?.isMobile()) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  });

  createEffect(
    on(
      () => menu?.isCollapsed(),
      (collapsed) => {
        if (collapsed && scrollContainerRef()) {
          scrollToTop();
        }
      }
    )
  );

  createEffect(
    on(
      () => menu?.activeParentMenuTitle(),
      () => {
        if (menu?.isCollapsed() && scrollContainerRef()) {
          scrollToTop();
        }
      }
    )
  );

  onMount(() => {
    window.addEventListener('resize', handleResize);
  });

  onCleanup(() => {
    window.removeEventListener('resize', handleResize);
    if (resizeObserver) {
      resizeObserver.disconnect();
    }
  });

  useOutsideClick(
    () => refMenuContainer(),
    () => menu?.isCollapsed() == true,
    () => menu?.setActiveMenuItem(undefined)
  );

  onCleanup(() => {
    document.body.style.overflow = '';
  });

  return {
    menu,
    isSidebarOpen,
    setIsSidebarOpen,
    toggleMenuCollapse,
    setRefMenuContainer,
    setScrollContainerRef,
    setMenuItemsRef,
    scrollHeight,
  };
};
